'use client';

import type { ButtonProps } from '@magicschool/spellbook/components/Button';
import { Button } from '@magicschool/spellbook/components/Button';
import type { IconButtonProps } from '@magicschool/spellbook/components/IconButton';
import { IconButton } from '@magicschool/spellbook/components/IconButton';
import type { SearchInputProps } from '@magicschool/spellbook/components/SearchInput';
import { SearchInput } from '@magicschool/spellbook/components/SearchInput';
import { Slot } from '@magicschool/spellbook/components/Slot';
import type { SpinnerProps } from '@magicschool/spellbook/components/Spinner';
import { Spinner } from '@magicschool/spellbook/components/Spinner';
import type { TableProps } from '@magicschool/spellbook/components/Table';
import type { TextProps } from '@magicschool/spellbook/components/Text';
import { Text } from '@magicschool/spellbook/components/Text';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { useDirection } from 'radix-ui/direction';
import { Slot as RadixSlot } from 'radix-ui/slot';
import type { ComponentProps } from 'react';
import { createContext, forwardRef, useContext, useMemo } from 'react';

export const tableVariants = cva('caption-bottom border-collapse', {
  variants: {
    /** Visual variant of the table. */
    variant: {
      default: '',
      banded: '',
    },
    /** Whether to wrap text in table cells. */
    wrapText: {
      true: '',
      false: '',
    },
    /** Whether the table should take the full width of its container. */
    fullWidth: {
      true: 'w-full',
      false: '',
    },
    /** Size/spacing variant for the table. */
    size: {
      sm: '',
      md: '',
    },
    /** Whether to apply hover styles to rows. */
    visibleHover: {
      true: '',
      false: '',
    },
    /** Vertical alignment of cells. */
    vAlignCells: {
      top: '',
      middle: '',
      bottom: '',
    },
  },
});

export const tableCellVariants = cva(
  'transition-colors ps-2 pe-2 inline-flex items-center flex-row flex-wrap gap-2 w-full overflow-hidden',
  {
    variants: {
      size: {
        md: 'py-2 min-h-table-row-h-md',
        sm: 'py-2 min-h-table-row-h-sm',
      },
      variant: {
        default: '',
        banded: '',
      },
      cellVariant: {
        text: 'text-start justify-start',
        number: 'text-end justify-end rtl:justify-start tabular-nums', // numbers are *always* right-aligned, even in RTL
        component: 'text-start justify-start',
        empty: 'text-center justify-center py-8  max-w-full flex-col gap-3',
      },
      wrapText: {
        true: 'whitespace-normal text-pretty break-words overflow-wrap',
        false: 'whitespace-nowrap text-pretty overflow-hidden',
      },
      align: {
        left: 'text-start justify-start',
        center: 'text-center justify-center',
        right: 'text-end justify-end rtl:text-start rtl:justify-start',
      },
    },
    compoundVariants: [
      {
        cellVariant: ['text', 'component', 'number'],
        align: 'right',
        className: 'text-end justify-end',
      },
      {
        cellVariant: 'number',
        align: 'left',
        className: 'text-start justify-start',
      },
    ],
  },
);

export const TableSlot = Slot<TableProps>({
  displayName: 'Table.Table',
  spellbookName: 'table-element',
  preventOverride: true,
  isRenderedByDefault: true,
  renderDefault: (props) => {
    const Table: React.FC<TableProps> = useMemo(
      () =>
        forwardRef(
          (
            {
              children,
              className,
              variant = 'banded',
              size = 'md',
              fullWidth,
              wrapText = true,
              visibleHover = false,
              vAlignCells = 'middle',
              id,
              ...restProps
            },
            forwardedRef,
          ) => {
            const { slots } = useSlots(children, {
              thead: { Component: TableHeadSlot, props: { variant, size, wrapText } },
              tbody: { Component: TableBodySlot, props: { variant, size, wrapText, visibleHover, vAlignCells } },
              tfoot: { Component: TableFootSlot, props: { variant, size, wrapText } },
              caption: { Component: TableCaptionSlot, props: { variant, size } },
            });

            return (
              <table
                ref={forwardedRef}
                data-spellbook-name="table-element"
                className={cn(tableVariants({ variant, size, wrapText, fullWidth }), className)}
                tabIndex={-1} // Make table programmatically focusable for a11y use cases
                {...restProps}
                id={id}
                key={id}
              >
                {slots.caption}
                {slots.thead}
                {slots.tbody}
                {slots.tfoot}
              </table>
            );
          },
        ),
      [],
    );

    return <Table {...props} />;
  },
});

export const TableTitleSlot = Slot<TextProps>({
  displayName: 'Table.Title',
  renderDefault: (props) => {
    const TitleComponent: React.FC<TextProps> = useMemo(
      () =>
        forwardRef(({ children, className, variant = 'heading-4', element = 'h4', ...restProps }, ref) => {
          return (
            <Text ref={ref} data-spellbook-name="table-title" variant={variant} element={element} className={cn(className)} {...restProps}>
              {children}
            </Text>
          );
        }),
      [],
    );

    return <TitleComponent {...props} />;
  },
});

export const TableSearchInputSlot = Slot<
  SearchInputProps & {
    leadingAdornment?: React.ReactNode;
  }
>({
  displayName: 'Table.SearchInput',
  preventOverride: true,
  renderDefault: ({ children, className, leadingAdornment, size = 'md', ...props }) => {
    return (
      <SearchInput
        data-spellbook-name="table-search-input"
        className={cn('w-full', className)}
        size={size}
        wrapperClassName="sm:max-w-75 lg:max-w-75"
        {...props}
      >
        {leadingAdornment && (
          <SearchInput.LeadingAdornment overrideDefault>
            <div className="sticky top-0">{leadingAdornment}</div>
          </SearchInput.LeadingAdornment>
        )}
      </SearchInput>
    );
  },
});

export const TableCaptionSlot = Slot<ComponentProps<'caption'>>({
  displayName: 'Table.Caption',
  preventOverride: true,
  renderDefault: ({ children, className, ...props }) => {
    return (
      <caption data-spellbook-name="table-caption" className={cn('py-2', className)} {...props}>
        <Text variant="caption-1" element="span" color="inherit">
          {children}
        </Text>
      </caption>
    );
  },
});

export const TableHeadSlot = Slot<ComponentProps<'thead'> & VariantProps<typeof tableVariants>>({
  displayName: 'Table.Head',
  preventOverride: true,
  renderDefault: ({ children, className, size, wrapText, ...props }) => {
    const { slots } = useSlots(children, {
      tr: { Component: TableRowSlot, props: { variant: 'default', size } },
    });

    return (
      <thead
        data-spellbook-name="table-head"
        className={cn('[&_tr]:border-b bg-surface-background-0 [&_tr]:border-border-default', className)}
        {...props}
      >
        {slots.tr}
      </thead>
    );
  },
});

const tableRowVariants = cva('transition-colors', {
  variants: {
    variant: {
      default: 'bg-surface-background-0 not-last:border-b border-border-default',
      banded: 'odd:bg-surface-background-2 even:bg-surface-background-0',
    },
    size: {
      md: '',
      sm: '',
    },
  },
});

export const TableBodySlot = Slot<ComponentProps<'tbody'> & VariantProps<typeof tableVariants>>({
  displayName: 'Table.Body',
  preventOverride: true,
  renderDefault: ({ children, className, variant, size, wrapText, visibleHover, vAlignCells, ...props }) => {
    const { slots } = useSlots(children, {
      tr: { Component: TableRowSlot, props: { variant, size, wrapText, vAlignCells } },
    });

    return (
      <tbody
        data-spellbook-name="table-body"
        className={cn(visibleHover && '[&_tr]:hover:bg-primary-muted-background', className)}
        {...props}
      >
        {slots.tr}
      </tbody>
    );
  },
});

export const TableFootSlot = Slot<ComponentProps<'tfoot'> & VariantProps<typeof tableVariants>>({
  displayName: 'Table.Foot',
  preventOverride: true,
  renderDefault: ({ children, className, variant, size, wrapText, ...props }) => {
    const { slots } = useSlots(children, {
      tr: { Component: TableRowSlot, props: { variant: 'default', size, wrapText } },
    });

    return (
      <tfoot data-spellbook-name="table-foot" className={cn('[&_tr]:border-t [&_tr]:border-border-default', className)} {...props}>
        {slots.tr}
      </tfoot>
    );
  },
});

export const TableRowSlot = Slot<ComponentProps<'tr'> & VariantProps<typeof tableVariants>>({
  displayName: 'Table.Row',
  preventOverride: true,
  renderDefault: ({ children, className, variant, wrapText, vAlignCells, size, ...props }) => {
    const { slots } = useSlots(children, {
      td: { Component: TableBodyCellSlot, props: { variant, size, wrapText, valign: vAlignCells } },
      th: { Component: TableHeadCellSlot, props: { variant, size, wrapText } },
    });

    return (
      <tr data-spellbook-name="table-row" className={cn(tableRowVariants({ variant, size }), className)} {...props}>
        {slots.th}
        {slots.td}
      </tr>
    );
  },
});

export const TableHeadCellSlot = Slot<ComponentProps<'th'> & VariantProps<typeof tableCellVariants> & { wrapperClassName?: string }>({
  displayName: 'Table.HeadCell',
  preventOverride: true,
  renderDefault: ({
    children,
    className,
    size,
    variant,
    cellVariant = 'text',
    wrapText,
    align: providedAlign,
    wrapperClassName,
    ...props
  }) => {
    const dir = useDirection();
    const align = providedAlign || (cellVariant === 'number' ? 'right' : dir === 'rtl' ? 'right' : 'left');

    const { slots, rest } = useSlots(children, {
      sort: { Component: TableHeadCellSortSlot },
    });

    /**
     * @TODO [SPE-37] Utility for post-slot prop updates
     * https://linear.app/magicschool/issue/SPE-37/utility-for-post-slot-prop-updates
     */
    if (rest) {
      const sortSlot = slots.sort;
      let sortSlotProps;

      if (sortSlot && typeof sortSlot === 'object' && 'props' in sortSlot) {
        sortSlotProps = {
          ...(sortSlot.props ?? {}),
          label: rest,
        };

        slots.sort = {
          ...sortSlot,
          props: {
            ...sortSlotProps,
          },
        };
      }
    }

    return (
      <th data-spellbook-name="table-head-cell" align={align} className={wrapperClassName} {...props}>
        <Text
          element="span"
          variant="label-2"
          className={cn(
            tableCellVariants({
              size,
              variant,
              cellVariant,
              wrapText,
              align,
            }),
            className,
          )}
          data-spellbook-name="table-head-cell-text"
        >
          <span className="whitespace-nowrap">{rest}</span>
          {slots.sort}
        </Text>
      </th>
    );
  },
});

type TableHeadCellSortProps = {
  direction?: 'asc' | 'desc';
  sortTextAsc?: string;
  sortTextDesc?: string;
  sortTextToggle?: string;
};

export const TableHeadCellSortSlot = Slot<IconButtonProps & TableHeadCellSortProps>({
  displayName: 'Table.HeadCellSort',
  preventOverride: true,
  renderDefault: ({
    direction,
    label,
    sortTextAsc = 'Sort ascending',
    sortTextDesc = 'Sort descending',
    sortTextToggle = 'Toggle sort',
    ...props
  }) => {
    const iconName = direction === 'asc' ? 'arrow-up' : direction === 'desc' ? 'arrow-down' : 'arrow-up-down';
    const iconLabelPart = direction === 'asc' ? sortTextAsc : direction === 'desc' ? sortTextDesc : sortTextToggle;
    const labelText = `${label ? `${label} - ` : ''}${iconLabelPart}`;

    return (
      <IconButton
        data-spellbook-name="table-head-cell-sort"
        data-state={direction}
        name={iconName}
        size="xs"
        color="neutral"
        variant="ghost"
        label={labelText}
        {...props}
      />
    );
  },
});

type TableBodyCellProps = ComponentProps<'td'> &
  VariantProps<typeof tableCellVariants> & {
    wrapperClassName?: string;
    /** Show an em dash in the border color when the cell renders nothing. Defaults to `true` for `text`/`number` cells, `false` for `component` cells. */
    emptyCell?: boolean;
  };

export const TableTextContext = createContext<{ emptyCellLabel?: string }>({});

const EMPTY_CELL_CLASSES = "empty:before:content-['—'_/_attr(data-empty-label)] empty:before:text-border-default";

export const TableBodyCellSlot = Slot<TableBodyCellProps>({
  displayName: 'Table.Cell',
  preventOverride: true,
  renderDefault: ({
    children,
    className,
    size,
    variant,
    cellVariant = 'text',
    wrapText = false,
    valign,
    align: providedAlign,
    wrapperClassName,
    emptyCell,
    ...props
  }) => {
    const dir = useDirection();
    const { emptyCellLabel = 'No value' } = useContext(TableTextContext);
    const align = providedAlign || (cellVariant === 'number' ? 'right' : dir === 'rtl' ? 'right' : 'left');
    const isTextual = cellVariant === 'text' || cellVariant === 'number';
    const truncates = !wrapText && isTextual;
    const showEmpty = emptyCell ?? isTextual;
    const emptyClassName = showEmpty ? EMPTY_CELL_CLASSES : undefined;
    const emptyLabel = showEmpty ? emptyCellLabel : undefined;

    return (
      <td data-spellbook-name="table-cell" valign={valign} align={align} className={wrapperClassName} {...props}>
        {cellVariant === 'component' ? (
          <div
            data-spellbook-name="table-component-item"
            data-empty-label={emptyLabel}
            className={cn(tableCellVariants({ size, variant, cellVariant, wrapText, align }), emptyClassName, className)}
          >
            {children}
          </div>
        ) : (
          <Text
            data-spellbook-name="table-text-item"
            element="span"
            variant="body-2"
            data-empty-label={truncates ? undefined : emptyLabel}
            className={cn(tableCellVariants({ size, variant, cellVariant, wrapText, align }), !truncates && emptyClassName, className)}
          >
            {truncates ? (
              <span className={cn('truncate', emptyClassName)} data-empty-label={emptyLabel}>
                {children}
              </span>
            ) : (
              <>{children}</>
            )}
          </Text>
        )}
      </td>
    );
  },
});

export const TableFilterChipsSlot = Slot<ComponentProps<'div'>>({
  displayName: 'Table.FilterChips',
  preventOverride: true,
  renderDefault: ({ children, className, ...props }) => {
    return (
      <div data-spellbook-name="table-filter-chips" className={cn('flex flex-wrap items-center gap-2', className)} {...props}>
        {children}
      </div>
    );
  },
});

export const TableActiveFiltersSlot = Slot<ComponentProps<'div'>>({
  displayName: 'Table.ActiveFilters',
  preventOverride: true,
  renderDefault: ({ children, className, ...props }) => {
    return (
      <div
        data-spellbook-name="table-active-filters"
        className={cn('flex mt-2 items-center flex-wrap gap-2 md:gap-4', className)}
        {...props}
      >
        {children}
      </div>
    );
  },
});

export const TablePaginationInfoSlot = Slot<ComponentProps<'div'>>({
  displayName: 'Table.PaginationInfo',
  preventOverride: true,
  renderDefault: ({ children, className, ...props }) => {
    return (
      <div data-spellbook-name="table-pagination-info" className={cn('flex items-center gap-2', className)} {...props}>
        {children}
      </div>
    );
  },
});

export const TablePaginationSlot = Slot<ComponentProps<'div'>>({
  displayName: 'Table.Pagination',
  preventOverride: true,
  renderDefault: ({ children, className, ...props }) => {
    return (
      <div data-spellbook-name="table-pagination" className={cn('ltr:ml-auto rtl:mr-auto', className)} {...props}>
        {children}
      </div>
    );
  },
});

export const TablePaginationJumpToSlot = Slot<ComponentProps<'div'>>({
  displayName: 'Table.PaginationJumpTo',
  preventOverride: true,
  renderDefault: ({ children, className, ...props }) => {
    return (
      <div data-spellbook-name="table-pagination-jump-to" className={cn('flex items-center gap-2', className)} {...props}>
        {children}
      </div>
    );
  },
});

export const TableActionsSlot = Slot<ComponentProps<'div'>>({
  displayName: 'Table.Actions',
  preventOverride: true,
  renderDefault: ({ children, className, ...props }) => {
    return (
      // biome-ignore lint/a11y/useSemanticElements: <fieldset> is for form controls; role="group" on a div is correct for action button groupings
      <div
        role="group"
        data-spellbook-name="table-actions"
        className={cn('flex items-center gap-2 ltr:ml-auto rtl:mr-auto', className)}
        {...props}
      >
        {children}
      </div>
    );
  },
});

export const TableSpinnerSlot = Slot<SpinnerProps>({
  displayName: 'Table.Spinner',
  isRenderedByDefault: true,
  renderDefault: ({ color = 'primary', size = 'lg', ...props }) => {
    /**
     * @TODO [SPE-15] Introduce Skeleton component
     * https://linear.app/magicschool/issue/SPE-15/introduce-skeleton-component
     */
    return (
      <div className="flex items-center justify-center w-full h-96" data-spellbook-name="table-spinner">
        <Spinner color={color} size={size} {...props} />
      </div>
    );
  },
});

export type TableActionSlotProps = ButtonProps & {
  /**
   * Whether to render children as the action element instead of Button.
   * Useful for rendering IconButton or other custom action components.
   */
  asChild?: boolean;
};

export const TableActionSlot = Slot<TableActionSlotProps>({
  displayName: 'Table.Action',
  renderDefault: ({ children, size = 'sm', variant = 'ghost', asChild, ...props }) => {
    const Comp = asChild ? RadixSlot : Button;
    const compProps = asChild ? {} : { size, variant };

    return (
      <Comp data-spellbook-name="table-action" {...compProps} {...props}>
        {children}
      </Comp>
    );
  },
});

export type TableBulkActionSlotProps = ButtonProps & {
  /**
   * Whether to render children as the action element instead of Button.
   * Useful for rendering IconButton or other custom action components.
   */
  asChild?: boolean;
};

export const TableBulkActionSlot = Slot<TableBulkActionSlotProps>({
  displayName: 'Table.BulkAction',
  renderDefault: ({ children, size = 'sm', variant = 'ghost', asChild, ...props }) => {
    const Comp = asChild ? RadixSlot : Button;
    const compProps = asChild ? {} : { size, variant };

    return (
      <Comp data-spellbook-name="table-bulk-action" {...compProps} {...props}>
        {children}
      </Comp>
    );
  },
});

export const TableSelectionBarSlot = Slot<ComponentProps<'div'>>({
  displayName: 'Table.SelectionBar',
  preventOverride: true,
  renderDefault: ({ children, className, ...props }) => {
    return (
      <div data-spellbook-name="table-selection-bar" className={cn(className)} {...props}>
        {children}
      </div>
    );
  },
});
